/**
 * QMS Patterns Panel
 * Displays all detected patterns in a scrollable list
 */

import { AlertTriangle, Filter, X } from 'lucide-react';
import { useState } from 'react';
import { useQmsTrendPatterns } from '../../hooks/useQmsTrends';
import { QmsPatternCard } from './QmsPatternCard';
import type { QmsPatternSeverity, QmsPatternType } from '../../types/qms.types';

const SEVERITY_OPTIONS: { value: QmsPatternSeverity; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const PATTERN_TYPE_OPTIONS: { value: QmsPatternType; label: string }[] = [
  { value: 'repeat_issue', label: 'Repeat Issues' },
  { value: 'location_hotspot', label: 'Location Hotspots' },
  { value: 'department_spike', label: 'Department Spikes' },
  { value: 'sla_breach_cluster', label: 'SLA Breaches' },
  { value: 'capa_overdue', label: 'CAPA Overdue' },
  { value: 'approval_bottleneck', label: 'Approval Bottlenecks' },
];

interface QmsPatternsPanelProps {
  onViewTickets?: (ticketIds: string[]) => void;
  onOpenDepartment?: (department: string) => void;
}

export function QmsPatternsPanel({ onViewTickets, onOpenDepartment }: QmsPatternsPanelProps) {
  const [selectedSeverities, setSelectedSeverities] = useState<QmsPatternSeverity[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<QmsPatternType[]>([]);

  const { data: patternsData, isLoading } = useQmsTrendPatterns({
    severity: selectedSeverities.length > 0 ? selectedSeverities : undefined,
    pattern_type: selectedTypes.length > 0 ? selectedTypes : undefined,
  });

  const patterns = patternsData?.patterns || [];
  const hasFilters = selectedSeverities.length > 0 || selectedTypes.length > 0;

  const toggleSeverity = (severity: QmsPatternSeverity) => {
    setSelectedSeverities((prev) =>
      prev.includes(severity)
        ? prev.filter((s) => s !== severity)
        : [...prev, severity]
    );
  };

  const toggleType = (type: QmsPatternType) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSelectedSeverities([]);
    setSelectedTypes([]);
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-32 bg-gray-200 rounded" />
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header with Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-indigo-600" />
            Detected Patterns
            {patterns.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                {patterns.length}
              </span>
            )}
          </h3>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="space-y-2">
          {/* Severity Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">Severity:</span>
            {SEVERITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleSeverity(option.value)}
                className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                  selectedSeverities.includes(option.value)
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Type Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">Type:</span>
            {PATTERN_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleType(option.value)}
                className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                  selectedTypes.includes(option.value)
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Patterns List */}
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {patterns.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium">
              {hasFilters
                ? 'No patterns match your filters'
                : 'No patterns detected in the latest analysis'}
            </p>
            <p className="text-xs mt-1">
              {hasFilters ? 'Try adjusting your filters' : 'This is a good sign!'}
            </p>
          </div>
        ) : (
          patterns.map((pattern, index) => (
            <QmsPatternCard
              key={`${pattern.pattern_type}-${index}`}
              pattern={pattern}
              onViewTickets={onViewTickets}
              onOpenDepartment={onOpenDepartment}
            />
          ))
        )}
      </div>
    </div>
  );
}
